import { isValid } from 'date-fns';
import { useMemo } from 'react';
import Icon from '@/components/Icon/Icon';
import InputBirthDay from '@/components/Input/InputBirthday';
import { HStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { useFormatDate } from '@/hooks/date';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { calculateAge } from '@/utils/calculate';
import { CFormItemBase, type CFormItemProps, buildCFormItem } from '../CFormItem';
type Props = CFormItemProps & {
  value?: Date;
  onChange?: (value: Date | undefined) => void;
};

/**
 * Card-based Form / BirthdayItem
 *
 * 読み取りモードでケーキアイコンと年齢を追加表示する。
 * 編集モードでは InputBirthDay（年/月/日個別入力）を使用する。
 */
export const CFormBirthdayItem = buildCFormItem<Props>((props: Props) => {
  const formatDate = useFormatDate();
  const {
    t,
    i18n
  } = useTranslation(defaultNS, {
    keyPrefix: 'features.cForm.birthdayItem'
  });
  const displayValue = useMemo(() => {
    if (!props.value) return '';
    return formatDate(props.value);
  }, [props.value, formatDate]);
  const dateString = useMemo(() => {
    if (!props.value) return null;
    const d = props.value;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }, [props.value]);
  const age = dateString ? calculateAge(dateString) : null;
  return <CFormItemBase {...props} key={`birthday-${i18n.language}`} readComponent={<HStack className="w-full items-center justify-between gap-2">
          <HStack className="items-center gap-1.5">
            <Icon iconName="CakeIcon" size={18} colorVariant="neutral" />
            <Text variant="labelLarge" className="text-neutral-on-fill">
              {displayValue || '-'}
            </Text>
          </HStack>
          {age !== null && !Number.isNaN(age) && <Text variant="labelLarge" className="text-neutral-muted-on-fill">
              {t('age', {
        count: age
      })}
            </Text>}
        </HStack>} writeComponent={<InputBirthDay value={dateString ?? undefined} onChange={v => {
    if (v === '') {
      props.onChange?.(undefined);
      return;
    }
    const parsed = new Date(v);
    if (isValid(parsed)) {
      props.onChange?.(parsed);
    }
  }} isRequired={props.required} />} />;
});